/* eslint-disable react/jsx-closing-tag-location */
'use client';

// This needs to be the first import in the file to ensure that WDYR is initialized before any other imports
import '../../../libs/common-web/util/wdyr';

import {
	ApiWrapper,
	appInfoRequestInterceptor,
	AppSubType,
	AppType,
	AppTypeContext,
	atomStore,
	FormBuilderContext,
	isPwaMode,
	languageRequestInterceptor,
	LocalizationProvider,
	LoggerProvider,
	paramsRequestInterceptor,
	QueryClientProvider,
	sessionAtom,
	sessionRequestInterceptor,
	type SociosApiInterceptors,
	SocketInitializer,
	StorageProvider,
	StrictModeProvider,
	useUserContext,
	useWaitingRoomSignal,
	waitingRoomRequestInterceptor,
	withThemeProvider
} from '@capsicum/common';
import {
	BlkSdkProvider,
	BrazePushNotificationProvider,
	env,
	ErrorServiceBoundary,
	FingerprintJSProvider,
	GlobalStylesProvider,
	LoaderWrapper,
	LocalStorageService,
	ModalWrapper,
	NetworkInfoProvider,
	NotificationsWrapper,
	OriginProvider,
	RtlHandler,
	setupLogger,
	StyledComponentsRegistry,
	TutorialProvider,
	useErrorResponseInterceptor,
	useFormBuilder,
	waitingRoomDetectInterceptor,
	WebPushProvider,
	WebServicesProvider,
	withSystemTheme
} from '@capsicum/common-web';
import { CustomReactQueryDevtools, DevTool, useACLDevProxyUrl } from '@capsicum/devkit/web';
import { FeedsListeners } from '@capsicum/features/feeds/shared';
import { NCWalletListeners } from '@capsicum/features/nc-wallet/shared';
import { V2FTMigrationSuccessSnackbar } from '@capsicum/features/nc-wallet/web';
import { usePartnerWebAppInitializer } from '@capsicum/features/partner-page/web';
import { GamificationSociosListeners, UserBalanceUpdateListeners } from '@capsicum/socios-core';
import { RewardPointsSnackbar, WalletConnectProvider } from '@capsicum/socios-core-web';
import { type Environment } from '@socios/translations';
import { createAsyncStoragePersister } from '@tanstack/query-async-storage-persister';
import { Provider as JotaiProvider } from 'jotai';
import { type FC, type PropsWithChildren, useEffect, useMemo, useState } from 'react';
import 'setimmediate';
import { ThemeProvider } from 'styled-components';

import Package from './../package.json';
import { RICH_TAGS } from './i18n';

import './styles.scss';

const storageService = new LocalStorageService();
// TODO Replace with a secured storage service
const securedStorageService = new LocalStorageService();

const socketUrl = env.NEXT_PUBLIC_WS_URL;

const apiConfig = {
	baseURL: env.NEXT_PUBLIC_API_URL,
	timeout: 20000
};

function registerServiceWorker() {
	if (typeof navigator !== 'undefined' && 'serviceWorker' in navigator) {
		navigator.serviceWorker.register('/socios-worker.js');
	}
}

const getAppSubType = () => (isPwaMode() ? AppSubType.pwa : AppSubType.standard);

const persister = createAsyncStoragePersister({
	storage:
		typeof window !== 'undefined'
			? {
					getItem: async (key: string) => localStorage.getItem(key),
					removeItem: async (key: string) => localStorage.removeItem(key),
					setItem: async (key: string, value: string) => localStorage.setItem(key, value)
				}
			: null
});

const Providers: FC<PropsWithChildren<{}>> = ({ children }) => {
	const ThemeWrapper = useMemo(() => withSystemTheme(withThemeProvider(ThemeProvider)), []);

	useUserContext();
	const { isPartnerWebApp } = usePartnerWebAppInitializer();

	const version = Package.version;
	const appType = isPartnerWebApp ? AppType.partnerWebApp : AppType.webApp;
	const appSubType = getAppSubType();
	const appHost = typeof window !== 'undefined' ? window.location.host : '';

	const errorResponseInterceptor = useErrorResponseInterceptor();
	const [logger] = useState(() => setupLogger());
	const getWaitingRoomSignal = useWaitingRoomSignal();

	const enhancedApiConfig = useACLDevProxyUrl(apiConfig);

	const apiInterceptor = useMemo<SociosApiInterceptors>(
		() => ({
			request: [
				waitingRoomRequestInterceptor(getWaitingRoomSignal),
				sessionRequestInterceptor({
					clear: async () => await atomStore.set(sessionAtom, null),
					get: async () => await atomStore.get(sessionAtom),
					set: async data => await atomStore.set(sessionAtom, data)
				}),
				languageRequestInterceptor(storageService),
				paramsRequestInterceptor(),
				appInfoRequestInterceptor(version, appType, appSubType)
			],
			response: [waitingRoomDetectInterceptor],
			responseError: errorResponseInterceptor
		}),
		[logger, errorResponseInterceptor]
	);

	useEffect(registerServiceWorker);

	return (
		<StyledComponentsRegistry>
			<LoggerProvider loggerService={logger}>
				<JotaiProvider store={atomStore}>
					<StrictModeProvider>
						<ApiWrapper config={enhancedApiConfig} interceptors={apiInterceptor}>
							<QueryClientProvider persister={persister} version={version}>
								<BlkSdkProvider
									appHost={appHost}
									appSubType={appSubType}
									appType={appType}
									version={version}
								>
									<ThemeWrapper>
										<GlobalStylesProvider>
											<StorageProvider
												securedStorageService={securedStorageService}
												storageService={storageService}
											>
												<ErrorServiceBoundary>
													<OriginProvider>
														<AppTypeContext.Provider value="webapp">
															<FingerprintJSProvider>
																<SocketInitializer sociosSocketUrl={socketUrl ?? ''}>
																	<GamificationSociosListeners
																		snackbarComponent={RewardPointsSnackbar}
																	/>
																	<FeedsListeners />
																	<NCWalletListeners
																		v2FTMigrationSnackbarComponent={
																			V2FTMigrationSuccessSnackbar
																		}
																	/>
																	<UserBalanceUpdateListeners />
																</SocketInitializer>
																<RtlHandler>
																	<LocalizationProvider
																		defaultRichTextElements={RICH_TAGS}
																		environment={
																			env.NEXT_PUBLIC_ENV! as Environment
																		}
																	>
																		<WebServicesProvider>
																			<ErrorServiceBoundary>
																				<BrazePushNotificationProvider>
																					<WebPushProvider deviceType="webapp">
																						<NetworkInfoProvider>
																							<WalletConnectProvider>
																								<TutorialProvider>
																									<FormBuilderContext.Provider
																										value={
																											useFormBuilder
																										}
																									>
																										{children}
																										<NotificationsWrapper />
																										<ModalWrapper />
																										<LoaderWrapper />
																										<DevTool />
																									</FormBuilderContext.Provider>
																								</TutorialProvider>
																							</WalletConnectProvider>
																						</NetworkInfoProvider>
																					</WebPushProvider>
																				</BrazePushNotificationProvider>
																			</ErrorServiceBoundary>
																		</WebServicesProvider>
																	</LocalizationProvider>
																</RtlHandler>
															</FingerprintJSProvider>
														</AppTypeContext.Provider>
													</OriginProvider>
												</ErrorServiceBoundary>
												<CustomReactQueryDevtools />
											</StorageProvider>
										</GlobalStylesProvider>
									</ThemeWrapper>
								</BlkSdkProvider>
							</QueryClientProvider>
						</ApiWrapper>
					</StrictModeProvider>
				</JotaiProvider>
			</LoggerProvider>
		</StyledComponentsRegistry>
	);
};

export default Providers;
